import { useContext, useEffect, useState, useRef } from "react";

import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  getUnavailableTimes,
  bookAppointment as book,
  updateAppointment as update,
} from "./service";
import { EVENT_34 } from "utils/tracking";

import Calendar from "react-calendar";

import { Popover } from "react-tiny-popover";

import AuthContext from "contexts/AuthContext";

import "react-calendar/dist/Calendar.css";
import { calculateDurationByWeight } from "lib/utils";
import { Button } from "components/button";
import stores from "consts/stores.json";

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

const extractStateFromReturnType = (returnType) => {
  if (!returnType) return null;
  const match = returnType.match(/in_store_(\w{2})/i);
  return match ? match[1].toUpperCase() : returnType.toUpperCase();
};

const getTimezoneForState = (returnType) => {
  const state = extractStateFromReturnType(returnType);
  const timezoneMap = {
    CO: "America/Denver", // MDT
    AR: "America/Chicago", // CDT
    NJ: "America/New_York", // EDT
    PA: "America/New_York", // EDT
  };
  return timezoneMap[state] || dayjs.tz.guess();
};

const getStoreByState = (stateCode) => {
  return stores.find((store) => store.state === stateCode);
};

const getAppointmentTimesForDay = (stateCode, selectedDate) => {
  const store = getStoreByState(stateCode);
  if (!store || !store.appointmentTimes) {
    // Fallback to default times if store not found
    return {
      start: { hour: 12, minute: 0 },
      end: { hour: 17, minute: 0 },
    };
  }

  const dayOfWeek = selectedDate.format("dddd").toLowerCase();
  return (
    store.appointmentTimes[dayOfWeek] || {
      start: { hour: 12, minute: 0 },
      end: { hour: 17, minute: 0 },
    }
  );
};

const generateTimeSlots = (
  selectedDate,
  unavailableTimes,
  duration,
  returnType
) => {
  const slots = [];

  // Get store-specific appointment times for the selected day
  const stateCode = extractStateFromReturnType(returnType);
  const appointmentTimes = getAppointmentTimesForDay(stateCode, selectedDate);

  const startHour = appointmentTimes.start.hour;
  const startMinute = appointmentTimes.start.minute;
  const endHour = appointmentTimes.end.hour;
  const timezone = getTimezoneForState(returnType);

  const hasStateTimezone = returnType
    ? ["CO", "AR", "NJ", "PA"].some((item) =>
        returnType.toUpperCase().includes(item)
      )
    : false;

  const now = dayjs().tz(timezone);

  // Ensure unavailableTimes is always an array
  const unavailableTimesArray =
    unavailableTimes && Array.isArray(unavailableTimes.appointments)
      ? unavailableTimes.appointments
      : [];

  for (let hour = startHour; hour <= endHour; hour++) {
    const startMinutes = hour === startHour ? startMinute : 0;
    for (let minutes = startMinutes; minutes < 60; minutes += duration) {
      const time = hasStateTimezone
        ? selectedDate.tz(timezone).hour(hour).minute(minutes).second(0)
        : selectedDate.hour(hour).minute(minutes).second(0);

      // Check if appointment would end after the store's closing time
      const appointmentEnd = time.add(duration, "minutes");
      const latestAllowedEnd = hasStateTimezone
        ? selectedDate
            .tz(timezone)
            .hour(appointmentTimes.end.hour)
            .minute(appointmentTimes.end.minute)
            .second(0)
        : selectedDate
            .hour(appointmentTimes.end.hour)
            .minute(appointmentTimes.end.minute)
            .second(0);

      if (appointmentEnd.isAfter(latestAllowedEnd)) {
        continue; // Skip this slot if it would end after store closing time
      }

      const isUnavailable = unavailableTimesArray.some((unavailable) => {
        const unavailableStart = dayjs(unavailable.time);
        const unavailableEnd = unavailableStart.add(
          unavailable.duration,
          "minutes"
        );

        // Check if the new appointment would overlap with existing appointments
        const newAppointmentStart = time;
        const newAppointmentEnd = time.add(duration, "minutes");

        // Check for overlap: new appointment starts before existing ends AND new appointment ends after existing starts
        return (
          newAppointmentStart.isBefore(unavailableEnd) &&
          newAppointmentEnd.isAfter(unavailableStart)
        );
      });

      slots.push({
        time: time.format("h:mm A"),
        disabled: time.isBefore(now) || isUnavailable,
      });
    }
  }
  return slots;
};

const formatDate = (date) => {
  return date.format("dddd, MMMM D");
};

const formatShortDate = (date) => {
  return date.format("ddd, D");
};

export const Scheduler = ({
  mode,
  title,
  subtitle,
  onTimeSelect,
  currentTime,
  appointmentId,
  weight,
  user,
  state,
  close = () => {},
  onComplete = () => {},
  takebackId,
  confirmLabel = "Confirm",
  onBookingError = () => {},
  currentAppointmentStatus,
  maxDate,
}) => {
  const hasStateTimezone = ["CO", "AR", "NJ", "PA"].some((item) =>
    state.toUpperCase().includes(item)
  );
  const { authTokens } = useContext(AuthContext);
  const accessToken = authTokens.access_token;

  const timezone = getTimezoneForState(state);

  const today = hasStateTimezone
    ? dayjs().tz(timezone).startOf("day")
    : dayjs().startOf("day");
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    hasStateTimezone ? today.tz(timezone) : today
  );
  const [unavailableTimes, setUnavailableTimes] = useState();
  const [selectedTime, setSelectedTime] = useState(null);
  const [pendingUpdate, setPendingUpdate] = useState(null);

  const calendarRef = useRef(null);

  const duration = calculateDurationByWeight(weight);

  useEffect(() => {
    getUnavailableTimes(selectedDate.format("YYYY-MM-DD"), state).then(
      (dates) => {
        setUnavailableTimes(dates);
      }
    );
  }, [selectedDate, state]);

  const bookAppointment = async (time, weight) => {
    const [hours, period] = time.split(" ");
    const [hour, minutes] = hours.split(":");
    let hour24 = parseInt(hour);

    // Convert to 24-hour format
    if (period === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (period === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    const hasStateTimezone = ["CO", "AR", "NJ", "PA"].some((item) =>
      state.toUpperCase().includes(item)
    );

    const appointmentTime = hasStateTimezone
      ? selectedDate.tz(timezone).hour(hour24).minute(parseInt(minutes))
      : selectedDate.hour(hour24).minute(parseInt(minutes));

    const booking = {
      start: appointmentTime.format(),
      duration,
      email: user?.email,
      firstName: user?.first_name,
      lastName: user?.last_name,
      weight,
    };

    return await book(booking, state);
  };

  const updateAppointment = (
    time,
    appointmentId,
    updateName,
    accessToken,
    takebackId
  ) => {
    const [hours, period] = time.split(" ");
    const [hour, minutes] = hours.split(":");
    let hour24 = parseInt(hour);

    // Convert to 24-hour format
    if (period === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (period === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    let appointmentTime;
    if (hasStateTimezone) {
      // Create a new date object: selectedDate + time in the timezone
      // e.g., "2025-06-30" + "11:00 AM" in "America/Denver"
      appointmentTime = dayjs.tz(
        `${selectedDate.format("YYYY-MM-DD")} ${hour24.toString().padStart(2, "0")}:${minutes.padStart(2, "0")}:00`,
        timezone
      );
    } else {
      // For non-timezone states, use local time
      appointmentTime = dayjs(selectedDate)
        .hour(hour24)
        .minute(parseInt(minutes))
        .second(0);
    }

    const booking = {
      time: appointmentTime.utc().format(),
    };

    update(appointmentId, booking, updateName, accessToken, takebackId);

    // If rescheduling a cancelled appointment, set status to confirmed
    if (currentAppointmentStatus === "cancelled") {
      update(appointmentId, { status: "confirmed" });
    }

    onComplete(booking);
  };

  const navigateDate = (direction) => {
    const newDate =
      direction === "next"
        ? selectedDate.add(1, "day")
        : selectedDate.subtract(1, "day");

    // Don't allow navigating to dates before today
    if (direction === "prev" && newDate.isBefore(today, "day")) {
      return;
    }

    // Don't allow navigating past maxDate if provided
    if (direction === "next" && maxDate) {
      const maxDateDayjs = hasStateTimezone
        ? dayjs(maxDate).tz(timezone)
        : dayjs(maxDate);
      if (newDate.isAfter(maxDateDayjs, "day")) {
        return;
      }
    }

    setUnavailableTimes();
    setSelectedDate(newDate);
  };

  const handleTimeSelect = async (slot) => {
    if (!slot.disabled) {
      setSelectedTime(slot.time);
      if (onTimeSelect) {
        try {
          const appointment = await bookAppointment(slot.time, weight);
          onTimeSelect({
            ...slot,
            selectedDate: selectedDate.format(),
            appointment,
          });
        } catch (error) {
          // If the error is due to slot unavailability, trigger the error callback
          if (error.code === "SLOT_UNAVAILABLE") {
            onBookingError();
            // Refresh the unavailable times to update the UI
            getUnavailableTimes(selectedDate.format("YYYY-MM-DD"), state).then(
              (dates) => {
                setUnavailableTimes(dates);
              }
            );
          } else {
            // Re-throw other errors
            throw error;
          }
        }
      } else {
        if (appointmentId) {
          if (mode === "edit") {
            setPendingUpdate({
              time: slot.time,
              appointmentId,
            });
          } else {
            updateAppointment(slot.time, appointmentId);
          }
        } else {
          bookAppointment(slot.time);
        }
      }
    }
  };

  const handleConfirmUpdate = () => {
    if (pendingUpdate) {
      updateAppointment(
        pendingUpdate.time,
        pendingUpdate.appointmentId,
        `appointmentRescheduledIntentAt`,
        accessToken,
        takebackId
      );
      setPendingUpdate(null);
    }
  };

  const handleCancelUpdate = () => {
    setPendingUpdate(null);
    setSelectedTime(null);
    close(null);
  };

  const timeSlots = generateTimeSlots(
    selectedDate,
    unavailableTimes,
    duration,
    state
  );

  const slotDurationFromTimeLabel = (subject) => {
    const minutes = parseInt(subject.replace(` `, `:`).split(`:`)[1]);

    return minutes === 30 ? 30 : 15;
  };

  console.log(timeSlots);

  const siblingAwareSlots = timeSlots.map((slot, index) => ({
    ...slot,
    disabled:
      duration > slotDurationFromTimeLabel(slot.time) &&
      timeSlots[index + 1] &&
      timeSlots[index + 1].disabled
        ? true
        : slot.disabled,
  }));

  return (
    <section className="max-w-4xl mx-auto p-6">
      {title && <h1 className="text-4xl font-bold mb-2">{title}</h1>}
      {subtitle && <h2 className="text-xl text-gray-600 mb-8">{subtitle}</h2>}

      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigateDate("prev")}
          disabled={selectedDate.isSame(today, "day")}
          className={`text-[#006CB7] ${
            selectedDate.isSame(today, "day")
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          <span className="mr-2">←</span>
          {formatShortDate(selectedDate.subtract(1, "day"))}
        </button>

        <Popover
          isOpen={showCalendar}
          align="center"
          positions={["bottom"]}
          onClickOutside={() => setShowCalendar(false)}
          content={
            <Calendar
              onChange={(date) => {
                setSelectedDate(
                  hasStateTimezone ? dayjs(date).tz(timezone) : dayjs(date)
                );
                setShowCalendar(false);
              }}
              value={new Date(selectedDate.toDate())}
              minDate={new Date(today.toDate())}
              maxDate={maxDate ? new Date(maxDate) : undefined}
            />
          }
        >
          <h2
            ref={calendarRef}
            className="text-2xl font-semibold cursor-pointer"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            {formatDate(selectedDate)}
          </h2>
        </Popover>

        <button
          onClick={() => navigateDate("next")}
          disabled={
            maxDate &&
            selectedDate.isSame(
              hasStateTimezone ? dayjs(maxDate).tz(timezone) : dayjs(maxDate),
              "day"
            )
          }
          className={`text-[#006CB7] ${
            maxDate &&
            selectedDate.isSame(
              hasStateTimezone ? dayjs(maxDate).tz(timezone) : dayjs(maxDate),
              "day"
            )
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          {formatShortDate(selectedDate.add(1, "day"))}
          <span className="ml-2">→</span>
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {siblingAwareSlots.map((slot, index) => (
          <button
            onClick={() => handleTimeSelect(slot)}
            key={index}
            disabled={slot.disabled}
            className={`
              p-4 rounded-lg text-center
              ${
                slot.disabled
                  ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                  : selectedTime === slot.time
                    ? "bg-blue text-white"
                    : "bg-white text-black"
              }
              ${unavailableTimes ? `` : ` opacity-0`}
            `}
            style={
              (!slot.disabled &&
                selectedTime !== slot.time && {
                  boxShadow: `inset 0 0 0 1px #000000`,
                }) ||
              {}
            }
          >
            {slot.time}
          </button>
        ))}
      </div>

      {mode === "edit" && (
        <div className="mt-8 flex justify-between gap-4">
          <button
            className="px-8 py-3 rounded-full border border-gray-400 text-gray-800 font-medium bg-white transition"
            onClick={handleCancelUpdate}
          >
            Cancel
          </button>
          <Button
            className={`px-8 py-3 rounded-full font-medium bg-blue-600 text-white transition ${!pendingUpdate ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={handleConfirmUpdate}
            disabled={!pendingUpdate}
            tracking={[EVENT_34]}
            overrideStyles
          >
            {confirmLabel}
          </Button>
        </div>
      )}
    </section>
  );
};
